'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { IChatLiteMessage, IChatResponse } from '@repo/sdk'
import { Button, Form, FormControl, FormField, FormItem, Input, Separator } from '@repo/ui'
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { SendFill } from 'react-bootstrap-icons'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

const schema = z.object({
  message: z
    .string()
    .min(1)
    .transform(data => data.trim()),
})

export const ChatInput = ({ form, isPending, onSubmit }: ReturnType<typeof useChatInput>) => {
  return (
    <div className={'flex w-full flex-col'}>
      <Separator />
      <Form {...form}>
        <form className={'flex w-full items-center gap-1 px-4 py-2'} onSubmit={onSubmit}>
          <FormField
            control={form.control}
            name={'message'}
            render={({ field }) => (
              <FormItem className={'w-full'}>
                <FormControl>
                  <Input
                    className={'h-45 border-none text-sm ring-0 focus-visible:ring-0'}
                    placeholder="Type a message"
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <Button
            aria-label={'Send message'}
            className={'ratio-1/1'}
            disabled={isPending}
            typeof={'submit'}
          >
            {/* Remove Bootstrap icon */}
            <SendFill color={'white'} rotate={'45deg'} size={24} />
            {isPending && <span className={'sr-only'}>Sending...</span>}
          </Button>
        </form>
      </Form>
    </div>
  )
}

export const useChatInput = ({
  chatId,
  isAdmin = false,
  loadId,
  sendMessage,
  userId,
}: {
  chatId: number
  isAdmin?: boolean
  loadId: number
  sendMessage: (message: string) => Promise<unknown>
  userId?: number
}) => {
  const queryClient = useQueryClient()

  const form = useForm({
    defaultValues: {
      message: '',
    },
    resolver: zodResolver(schema),
  })

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: z.infer<typeof schema>) => {
      queryClient.setQueryData<InfiniteData<IChatResponse>>(
        [`chat-${chatId}`, `user-${userId}`, `load-${loadId}`, `isAdmin-${isAdmin}`],
        oldData => {
          // Ensure userId is present
          if (!userId) {
            console.error('Optimistic update failed: userId is missing.')
            return oldData
          }

          // Construct the new message object matching the API structure
          const newMessage: IChatLiteMessage = {
            created_at: new Date().toISOString(),
            message: data.message,
            // Populate the user object with the sender's details
            // IMPORTANT: Ensure currentUserData has the correct structure
            user: {
              id: userId,
            },
            user_id: userId,
            user_id_no_read: 1,
          }

          // If there's no existing data or pages, create the structure
          if (!oldData || !oldData.pages || oldData.pages.length === 0) {
            return {
              pageParams: [undefined], // Or the initial page param used
              pages: [
                {
                  data: [newMessage],
                  // Initialize meta as best as possible, or use defaults
                  meta: {
                    current_page: 1,
                    from: 1,
                    last_page: 1,
                    per_page: 15,
                    to: 1,
                    total: 1,
                  },
                },
              ],
            }
          }

          // Otherwise, add the new message to the *first* page's data array
          return {
            ...oldData,
            pages: oldData.pages.map((page, index) => {
              if (index === 0) {
                // Create a new page object for the first page
                return {
                  ...page,
                  // Create a new data array with the new message prepended
                  data: [newMessage, ...page.data],
                  // Optionally update meta if needed (e.g., increment total)
                  meta: {
                    ...page.meta,
                    // Adjust 'to' if applicable and not null
                    to: page.meta.to ? page.meta.to + 1 : 1,
                    total: (page.meta?.total ?? 0) + 1,
                  },
                }
              }
              // Return other pages unchanged
              return page
            }),
          }
        },
      )
      await sendMessage(data.message)
    },
    onSuccess: async () => {
      form.reset()
      await queryClient.invalidateQueries({
        queryKey: [`chat-${chatId}`, `user-${userId}`, `load-${loadId}`, `isAdmin-${isAdmin}`],
      })
    },
  })

  return { form, isPending, onSubmit: form.handleSubmit(value => mutate(value)) }
}
